import { createFileRoute } from "@tanstack/react-router";
import { Helmet } from "react-helmet-async";
import {
  UsersRoundIcon,
  UsersIcon,
  TrendingUpIcon,
  PercentIcon,
  ClipboardCheckIcon,
  SettingsIcon,
} from "lucide-react";
import { PageHeader } from "@/components/ui/page-header";
import {
  ActionCard,
  ActionCardGrid,
  ActionCardSection,
} from "@/components/ui/action-card";

export const Route = createFileRoute("/portal/hr/")({
  component: HrDashboard,
});

interface HrFeature {
  title: string;
  description: string;
  icon: typeof UsersRoundIcon;
  href: string;
  iconColor: string;
  /** Card is shown but not yet clickable — its feature page ships in a follow-up. */
  comingSoon?: boolean;
}

const features: HrFeature[] = [
  {
    title: "Onboarding",
    description:
      "Invite new employees, capture their details, and push them through to SimplePay.",
    icon: UsersRoundIcon,
    href: "/portal/hr/onboarding",
    iconColor: "bg-emerald-500",
  },
  {
    title: "Employees",
    description:
      "Browse the employee directory synced to your payroll and cost-allocation data.",
    icon: UsersIcon,
    href: "/portal/hr/employees",
    iconColor: "bg-blue-500",
  },
  {
    title: "Forecasting",
    description:
      "Model payroll cost scenarios and forecast spend across pay points and waves.",
    icon: TrendingUpIcon,
    href: "/portal/hr/forecast",
    iconColor: "bg-purple-500",
  },
  {
    title: "Cost Allocations",
    description:
      "Split employee payroll costs across departments, projects, and cost centres.",
    icon: PercentIcon,
    href: "/portal/hr/allocations",
    iconColor: "bg-amber-500",
  },
  {
    title: "Rules",
    description:
      "Configure the increase rules and adjustments that drive payroll forecasts.",
    icon: ClipboardCheckIcon,
    href: "/portal/hr/rules",
    iconColor: "bg-rose-500",
  },
  {
    title: "Settings",
    description:
      "Name your organisation and label the cost-allocation dimensions.",
    icon: SettingsIcon,
    href: "/portal/hr/settings",
    iconColor: "bg-slate-600",
  },
];

function HrDashboard() {
  return (
    <>
      <Helmet>
        <title>HR Lite | Portal</title>
      </Helmet>

      <div className="space-y-6">
        <PageHeader
          title="HR Lite & Payroll"
          description="Onboard employees, manage your payroll data, and forecast costs — all in one place."
          icon={UsersRoundIcon}
          eyebrow="People"
          gradient
        />

        <ActionCardSection title="Your Services">
          <ActionCardGrid columns={3}>
            {features.map((feature) => (
              <ActionCard
                key={feature.href}
                title={feature.title}
                description={
                  feature.comingSoon
                    ? `${feature.description} (Coming soon)`
                    : feature.description
                }
                icon={feature.icon}
                href={feature.href}
                iconColor={feature.iconColor}
                disabled={feature.comingSoon}
                testId={`hr-card-${feature.href.split("/").pop()}`}
              />
            ))}
          </ActionCardGrid>
        </ActionCardSection>
      </div>
    </>
  );
}
